// src/components/modals/FolderModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, 
  Folder, 
  Loader2, 
  Save,
  BarChart3,
  TrendingUp,
  DollarSign,
  Users,
  Briefcase,
  Target,
  Zap,
  Star,
  FileText,
  Package
} from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { apiCall } from '../../utils/api';

export default function FolderModal({ isOpen, onClose, folder = null, onSuccess }) {
  const isEditing = !!folder;
  
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [icon, setIcon] = useState('Folder');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && folder) {
      // Editing existing folder
      setName(folder.name || '');
      setColor(folder.color || '#3B82F6');
      setIcon(folder.icon || 'Folder');
    } else if (isOpen) {
      // Creating new folder
      setName('');
      setColor('#3B82F6');
      setIcon('Folder');
    }
  }, [isOpen, folder]);

  async function handleSave() {
    if (!name.trim()) {
      alert('Please enter a folder name');
      return;
    }

    setSaving(true);
    try {
      const url = isEditing ? `/charts/folders/${folder.id}` : '/charts/folders/';
      const method = isEditing ? 'PUT' : 'POST';
      
      const res = await apiCall(url, {
        method,
        body: JSON.stringify({
          name: name.trim(),
          color,
          icon
        })
      });

      if (res.ok) {
        if (onSuccess) onSuccess();
        onClose();
      } else {
        const error = await res.json();
        alert(error.detail || `Failed to ${isEditing ? 'update' : 'create'} folder`);
      }
    } catch (err) {
      console.error(`Failed to ${isEditing ? 'update' : 'create'} folder:`, err);
      alert(`Failed to ${isEditing ? 'update' : 'create'} folder`);
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  // Color options
  const colorOptions = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Green', value: '#10B981' },
    { name: 'Yellow', value: '#F59E0B' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Gray', value: '#6B7280' },
  ];

  // Icon options - using Lucide icons
  const iconOptions = [
    { name: 'Folder', icon: Folder },
    { name: 'Chart', icon: BarChart3 },
    { name: 'Trending', icon: TrendingUp },
    { name: 'Dollar', icon: DollarSign },
    { name: 'Users', icon: Users },
    { name: 'Briefcase', icon: Briefcase },
    { name: 'Target', icon: Target },
    { name: 'Zap', icon: Zap },
    { name: 'Star', icon: Star },
    { name: 'Document', icon: FileText },
    { name: 'Package', icon: Package },
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl shadow-2xl max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#2a2a2a]">
            <div className="flex items-center gap-2">
              <Folder className="w-5 h-5 text-blue-500" />
              <h2 className="text-xl font-semibold text-white">
                {isEditing ? 'Edit Folder' : 'Create Folder'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#2a2a2a] rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-[#a0a0a0]" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Folder Name */}
            <div>
              <label className="text-sm font-medium text-white block mb-2">
                Folder Name *
              </label>
              <input
                type="text"
                placeholder="e.g., Sales Dashboard"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full h-10 px-3 bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 placeholder:text-[#4a4a4a]"
                autoFocus
              />
            </div>

            {/* Color Picker */}
            <div>
              <label className="text-sm font-medium text-white block mb-2">
                Color
              </label>
              <div className="grid grid-cols-7 gap-2">
                {colorOptions.map((colorOption) => (
                  <button
                    key={colorOption.value}
                    onClick={() => setColor(colorOption.value)}
                    className={`
                      w-10 h-10 rounded-lg transition-all
                      ${color === colorOption.value 
                        ? 'ring-2 ring-white ring-offset-2 ring-offset-[#1a1a1a] scale-110' 
                        : 'hover:scale-105'
                      }
                    `}
                    style={{ backgroundColor: colorOption.value }}
                    title={colorOption.name}
                  />
                ))}
              </div>
            </div>

            {/* Icon Picker */}
            <div>
              <label className="text-sm font-medium text-white block mb-2">
                Icon
              </label>
              <div className="grid grid-cols-6 gap-2">
                {iconOptions.map((iconOption) => {
                  const IconComponent = iconOption.icon;
                  const isSelected = icon === iconOption.name;
                  return (
                    <button
                      key={iconOption.name}
                      onClick={() => setIcon(iconOption.name)}
                      className={`
                        h-12 rounded-lg flex items-center justify-center transition-all
                        ${isSelected
                          ? 'bg-blue-500/20 ring-2 ring-blue-500 scale-105' 
                          : 'bg-[#0a0a0a] border border-[#2a2a2a] hover:bg-[#2a2a2a] hover:scale-105'
                        }
                      `}
                      title={iconOption.name}
                    >
                      <IconComponent className={`w-5 h-5 ${isSelected ? 'text-blue-500' : 'text-[#a0a0a0]'}`} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Preview */}
            <div className="pt-4 border-t border-[#2a2a2a]">
              <label className="text-sm font-medium text-white block mb-2">
                Preview
              </label>
              <div className="flex items-center gap-3 p-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: color + '20' }}
                >
                  {(() => {
                    const selectedIcon = iconOptions.find(opt => opt.name === icon);
                    const IconComponent = selectedIcon ? selectedIcon.icon : Folder;
                    return <IconComponent className="w-5 h-5" style={{ color: color }} />;
                  })()}
                </div>
                <span className="text-white font-medium">
                  {name || 'Folder Name'}
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-[#2a2a2a]">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Update Folder' : 'Create Folder'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}